import throttle from 'lodash.throttle';
import EventEmitter from 'events';

export default class InfinityScroll extends EventEmitter {
  constructor(options = {}) {
    super();

    /**
     * Whether InfinityScroll should be spitting out debug data in console or not.
     */
    this.debug = process.env.NODE_ENV !== 'production' ? true : false;

    this.log('LOADING');

    /**
     * Base URL for the Labrador API.
     */
    let apiBaseUrl = process.env.API_BASE_URL;

    if (apiBaseUrl == false || apiBaseUrl == '') {
      this.error('apiBaseUrl not set. Check .env file.');
      return;
    }

    if (document.body.dataset.disableInfinityScroll) {
      this.log('Disabled');
      return;
    }

    if (window.isEditMode) {
      this.log('EditMode: Disabled');
      return;
    }

    /**
     * Sets what type of page we are scrolling.
     */
    this.pageType = document.body.dataset.pageType;

    /**
     * Should be set to whatever hostpath the front 'index' page has.
     */
    this.frontPageHostpath = 'index';

    /**
     * How often to fire the 'scroll' event.
     */
    this.scrollThrottle = 150;

    let loadOffset = parseInt(document.body.dataset.infinityscrollLoadOffset || '100') / 100;

    /**
     * How far before reaching a new breakpoint should it load?
     */
    this.loadBreakpointOffset = window.innerHeight * loadOffset;

    /**
     * Keep track of the element where new content should be injected.
     */
    this.contentContainer = false;

    /**
     * Keep track of every breakpoint created
     */
    this.breakpoints = [];

    /**
     * Keep track of the current breakpoint.
     */
    this.currentBreakpoint = false;

    /**
     * Keep track of the content that has been injected
     */
    this.content = [];

    /**
     * Store the next content to be injected
     */
    this.nextContent = false;

    /**
     * A callback function that can be overwritten to use a customized query.
     */
    this.nextArticleQueryCallback = options.nextArticleQueryCallback || ((current, query) => query);

    /**
     * Whether new content is being injected and a loader should be shown.
     */
    this.isLoading = false;

    /**
     * Keeps a copy of the loader element.
     */
    this.loader = false;

    /**
     * Holds a custom scroller object if configured.
     */
    this.scroller = options.scroller || false;

    this.newArticleScroll = options.newArticleScroll || false;

    /**
     * Check slash ending of API url
     */
    this.apiBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/';

    this.on('init-ready', () => {
      this.log('INIT READY');

      // Enable scroll listener
      if (this.scroller !== false) {

        this.log('Custom scroller detected. Using custom.')
        this.scroller.on('tick', () => {
          this.scroll();
        })

      } else {

        this.log('No scroller configured. Using own.')
        window.addEventListener(
          'scroll',
          throttle(() => {
            this.scroll();
          }, this.scrollThrottle)
        );

      }

      // Smoother reload
      window.onbeforeunload = () => {
        window.scrollTo(0, 0);
      };
    });

    this.on('current-breakpoint-change', (breakpoint) => {
      this.log('CURRENT BREAKPOINT CHANGED', breakpoint);
      this.setHistory(breakpoint);
    });

    this.on('content-next-ready', (content) => {
      this.log('NEXT CONTENT READY', content);
    });

    this.on('content-next-empty', () => {
      this.log('NO MORE CONTENT TO LOAD');
    });

    this.on('new-content-loaded', (content) => {
      this.log('CONTENT LOADED', content);
    });

    switch (this.pageType) {
      case 'front':
        this.frontInit();
        break;
      case 'article':
        if ( this.newArticleScroll) {
          this.articleTeaserBoxInit();
        } else {
          this.articleInit();
        }
        break;
      default:
        console.warn('pageType not valid', this.pageType);
    }
  }

  /**
   * Wrapper function for console.log. Used together with debug.
   *
   * @param  {...any} args
   */
  log(...args) {
    if (this.debug) {
      console.log('InfinityScroll', '-', ...args);
    }
  }

  /**
   * Wrapper function for console.error.
   *
   * @param  {...any} args
   */
  error(...args) {
    console.error('InfinityScroll', '-', ...args);
  }

  /**
   * Set the current loading status. If no loader is defined, create a basic one.
   *
   * @param {Boolean} willLoad
   */
  setLoading(willLoad) {
    if (this.loader === false) {
      this.loader = document.createElement('div');
      this.loader.classList.add('infinityscroll-loader');
    }

    if (willLoad) {
      this.isLoading = true;
      this.contentContainer.appendChild(this.loader);
    } else {
      this.isLoading = false;
      this.loader.parentNode.removeChild(this.loader);
    }
  }

  /**
   * Set the current browser history to the breakpoint.
   *
   * @param {Object} breakpoint
   */
  setHistory(breakpoint) {
    this.log('SET HISTORY', breakpoint.dataset.url);
    window.history.replaceState({}, breakpoint.dataset.title, breakpoint.dataset.url);
    document.title = breakpoint.dataset.title;
  }

  /**
   * Create & Append a new breakpoint element.
   *
   * @param {Object} data
   */
  appendBreakpoint(data) {
    let breakpointElement = document.createElement('div');
    breakpointElement.classList.add('infinityscroll-breakpoint');

    breakpointElement.dataset.id = data.id;
    breakpointElement.dataset.title = data.title;
    breakpointElement.dataset.section = data.section_tag || '';

    if (this.pageType == 'article') {
      let dataUrl = new URL(data.published_url, data.siteDomain);
      if (this.nativeHostnameRegexCheck.test(dataUrl.hostname)) {
        dataUrl.searchParams.set('native', '1');
      }
      breakpointElement.dataset.url = dataUrl.pathname + dataUrl.search + dataUrl.hash;

      if (data.verticals) {
        breakpointElement.dataset.verticals = data.verticals;
      }
    }

    if (this.pageType == 'front') {
      breakpointElement.dataset.url = data.url;
    }

    this.breakpoints.push(breakpointElement);

    if (this.currentBreakpoint == false) {
      this.currentBreakpoint = breakpointElement;
    }

    this.contentContainer.appendChild(breakpointElement);
  }

  /**
   * Triggers a scroll-trick and check whether new content should be loaded or a breakpoint changed.
   */
  scroll() {
    this.emit('scroll-tick');
    this.maybeLoadNextContent();
    this.maybeUpdateCurrentBreakpoint();
  }

  /**
   * Check whether user has scrolled far enough that new content should be loaded
   */
  maybeLoadNextContent() {
    if (this.isLoading) {
      return;
    }

    let breakpoints = [...this.breakpoints];

    let lastBreakpoint = breakpoints.pop();
    let lastBreakpointPosition = lastBreakpoint.getBoundingClientRect().top + window.scrollY;
    let loadNewBreakpointPosition = this.scrollPosition() + this.loadBreakpointOffset;

    if (loadNewBreakpointPosition > lastBreakpointPosition) {
      this.loadNextContent();
    }
  }

  /**
   * Load new content.
   */
  loadNextContent() {
    this.setLoading(true);

    switch (this.pageType) {
      case 'front':
        this.loadNextFront();
        break;
      case 'article':
        this.loadNextArticle();
        break;
    }
  }

  /**
   * Check which breakpoint the user is currently viewing.
   */
  maybeUpdateCurrentBreakpoint() {
    let scrollPosition = this.scrollPosition('mid');

    for (const breakpoint of this.breakpoints) {
      let breakpointPosition = breakpoint.getBoundingClientRect().top + window.scrollY;

      if (scrollPosition < breakpointPosition) {
        if (breakpoint !== this.currentBreakpoint) {
          this.currentBreakpoint = breakpoint;
          this.emit('current-breakpoint-change', breakpoint);
        }
        break;
      }
    }
  }

  /**
   * Helper function for getting current scroll position. Takes 'top' (default), 'mid' and 'bot'.
   *
   * @param {String} pos
   */
  scrollPosition(pos = 'top') {
    if (pos == 'top') {
      return window.scrollY;
    }
    if (pos == 'mid') {
      return window.scrollY + window.innerHeight / 2;
    }
    if (pos == 'bot') {
      return window.scrollY + window.innerHeight;
    }
  }

  /**
   * We are scrolling a frontpage. Run through initialization.
   */
  frontInit() {
    this.log('FRONT INIT');

    this.pageToScrollId = document.body.dataset.infinityscrollid || false;
    this.hostpath = document.body.dataset.hostpath || false;
    this.originalTitle = document.title;
    this.contentContainer = document.querySelector('div#content section.main');

    if (this.pageToScrollId === false) {
      this.error('frontInit', "pageToScrollId can't be determined.");
      return;
    }
    if (this.hostpath === false) {
      this.error('frontInit', "hostpath can't be determined.");
      return;
    }

    this.hostpath = this.hostpath == this.frontPageHostpath ? '/' : `/${this.hostpath}/`;

    let originalArticles = Array.from(this.contentContainer.querySelectorAll('article'));
    let originalArticlesFiltered = originalArticles.filter((element) => {
      return element.dataset.id != undefined && element.dataset.id != '';
    });

    this.originalArticlesIds = originalArticlesFiltered.map((element) => {
      return element.dataset.id;
    });

    // Check if we are loading a paged page.
    let regex = /\/page\/([0-9]+)/g;
    let pagedPage = regex.exec(window.location.pathname);
    let startingPage = 1;

    if (pagedPage) {
      startingPage = parseInt(pagedPage[1]) - 1; // Minus 1 to compensate for removal of inital page.
      for (const element of this.contentContainer.childNodes) {
        if (element instanceof Element) {
          element.style.display = 'none'; // Hide initial articles but keep in dom.
        }
      }
    }

    let startingUrl = startingPage == 1 ? this.hostpath : `${this.hostpath}page/${startingPage}`;
    let title = startingPage == 1 ? this.originalTitle : `${this.originalTitle} - Sida ${startingPage}`;

    this.nextContent = {
      id: startingPage,
      url: startingUrl,
      title: title,
    };

    this.appendBreakpoint(this.nextContent);
    this.content.push(this.nextContent);
    this.readyNextFront();

    this.emit('init-ready');

    // If we are loading a paged page then we should go ahead and load it
    if (pagedPage) {
      this.loadNextContent();
    }
  }

  /**
   * Set up data needed to load the next page of a frontpage.
   */
  readyNextFront() {
    let newPageNum = this.nextContent.id + 1;

    let nextContent = {
      id: newPageNum,
      url: `${this.hostpath}page/${newPageNum}`,
      title: `${this.originalTitle} - Sida ${newPageNum}`,
    };

    this.nextContent = nextContent;
    this.emit('content-next-ready', this.nextContent);
  }

  /**
   * Load the next front page.
   */
  loadNextFront() {
    if (this.nextContent !== false) {
      let self = this;
      let pageToLoad = this.nextContent.id - 1;
      let baseUrl = this.apiBase + 'fragment/structure/?';
      let data = {
        pageId: this.pageToScrollId,
        path: '/dropzone',
        structureType: 'row',
        count: 0,
        contentFormat: 'markup',
        page: pageToLoad,
      }

      let search = new URLSearchParams(data);

      for (let articleId of this.originalArticlesIds) {
        search.append("excludeArticles[]", articleId);
      }

      fetch(baseUrl + search.toString())
        .then((response) => {
          return response.text()
        })
        .then((data) => {
          const parser = new DOMParser();
          let parsedHTML = parser.parseFromString(data, 'text/html');
          let container = document.createElement('div');

          for(var i = 0; i < parsedHTML.body.childNodes.length; i++) {
            container.appendChild(parsedHTML.body.childNodes[i]);
          }

          if (container.querySelectorAll('article').length == 0) {
            self.nextContent = false;
            self.emit('content-next-empty');
          } else {
            document.querySelector('div#content section.main').appendChild(container);
            self.appendBreakpoint(self.nextContent);

            self.content.push(self.nextContent); // Set content to loaded
            self.emit('new-content-loaded', container);

            self.maybeUpdateCurrentBreakpoint();
            self.readyNextFront();
          }
          self.setLoading(false);

        }).catch((err) => {
          self.setLoading(false);
          this.error('loadNextFront', err);
        });
    } else {
      this.log('END OF FRONT');
      this.setLoading(false);
    }
  }

  /**
   * We are scrolling an article. Run through article initialization
   */
  articleInit() {
    this.log('ARTICLE INIT');

    this.contentContainer = document.querySelector('div#content');

    let originalArticleElement = document.querySelector('div#content article');

    this.originalArticleId = originalArticleElement.dataset.id;
    this.originalArticleSection = originalArticleElement.dataset.section;
    this.originalArticleVertical = originalArticleElement.dataset.vertical ;
    this.siteId = document.body.dataset.siteId;

    let load_type = document.body.dataset.infinityscrollLoadType || 'latest_any';


    // Set article scroll type
    if (load_type == 'latest_section') {
      this.articleScrollType = 'section';
    } else if (load_type == 'latest_vertical') {
      this.articleScrollType = 'vertical';
    } else {
      this.articleScrollType = 'any';
    }

    this.nativeHostnameRegexCheck = /^[a-z]*native/;

    let baseUrl = this.apiBase + 'article/?';

    let data = {
      query: 'id:' + this.originalArticleId,
    };

    fetch(baseUrl + new URLSearchParams(data))
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        this.nextContent = data.result[0];
        this.appendBreakpoint(this.nextContent);
        this.content.push(this.nextContent);
        this.readyNextArticle();
        this.emit('init-ready');
      }).catch((err) => {
        this.error('articleInit', err);
      });
  }

  /**
   * Set up data needed to load next article.
   */
  readyNextArticle() {
    let query = 'visibility_status:P AND published:[* NOW-70MINUTES] ';
    if (this.articleScrollType == 'section' && this.originalArticleSection != undefined) {
      let querySection = this.originalArticleSection;

      if (/\s/.test(querySection)) {
        querySection = '"' + querySection + '"';
      }

      query += 'AND section:' + querySection;
    }

    // Add query to restict articles to same vertical as original
    if (this.articleScrollType == 'vertical' && this.originalArticleVertical != undefined) {
      let queryVertical = this.originalArticleVertical;

      if (/\s/.test(queryVertical)) {
        queryVertical = '"' + queryVertical + '"';
      }

      query += 'AND verticalHost:' + queryVertical;
    }

    if (this.content.length > 0) {
      let not = ' AND NOT id:(';
      for (const articleData of this.content) {
        not += articleData.id + ' OR ';
      }
      not = not.substring(0, not.length - 4) + ')';
      query = query.concat(not);
    }

    query = this.nextArticleQueryCallback(this.nextContent, query)

    if (query === false || typeof query === 'undefined') {
      this.nextContent = false;
      return
    }

    if (typeof query === 'number') {
      query = query.toString();
    }

    let data = {}
    if (!isNaN(query) && parseInt(Number(query)) == query && !isNaN(parseInt(query, 10))) {
      data = {
        query: 'id:' + query,
      }
    } else {
      data = {
        query: query,
        orderBy: 'published',
        limit: 1,
        site_id: this.siteId,
      };
      
      // Temporary fix until Elle native site is merged into main site
      // Include both main site and native site for Elle in article infinity scroll
      if ((this.siteId == '17' || this.siteId == '18') && window.location.href.includes('www-staging.elle.se')) {
        data.query = data.query.concat(' AND lab_site_id:(17 OR 18)');
        delete data.site_id;
      }
      if ((this.siteId == '14' || this.siteId == '15') && window.location.href.includes('www.elle.se')) {
        data.query = data.query.concat(' AND lab_site_id:(14 OR 15)');
        delete data.site_id;
      }
    }

    let baseUrl = this.apiBase + 'article/?';

    fetch(baseUrl + new URLSearchParams(data))
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        if (data.result.length > 0) {
          this.nextContent = data.result[0];
          this.emit('content-next-ready', this.nextContent);
        } else {
          this.nextContent = false;
          this.emit('content-next-empty');
        }
      }).catch((err) => {
        this.error('readyNextArticle', err);
      });
  }

  /**
   * Load the next article.
   */
  loadNextArticle() {
    if (this.nextContent !== false) {
      let self = this;

      let published_url = self.nextContent.published_url;

      if(self.nextContent.siteDomain.indexOf('//native') !== -1) {
        published_url = `${published_url}?native=1`;
      }

      fetch(published_url)
        .then((response) => {
          return response.text()
        })
        .then((data) => {
          const parser = new DOMParser();
          let doc = parser.parseFromString(data, 'text/html');
          let article = doc.querySelector('div#content article');

          this.contentContainer.appendChild(article); // Add content to DOM
          this.appendBreakpoint(this.nextContent); // Add new breakpoint
          this.content.push(this.nextContent); // Set content to loaded
          this.emit('new-content-loaded', article);
          this.readyNextArticle();
          this.setLoading(false);

        }).catch((err) => {
          self.setLoading(false);
          this.error('articleInit', err);
        });

    } else {
      this.log('END OF ARTICLES');
      this.setLoading(false);
    }
  }
  // Init teaserbox instead of infinityscroll on articles
  articleTeaserBoxInit() {
    this.log('ARTICLE TEASERBOX INIT');
    let hasLoaded = false;
    const article = document.querySelector('.o-row');
    const section = article.getAttribute('data-section');
    const articleId = article.getAttribute('data-id');
    const container = document.querySelector('.teaser-box');
    const options = window.scrollBoxOptions;
    const selected = options[section] || options['default'];
    const boxTitle = options[section] && options[section].boxTitle ? options[section].boxTitle : false;
    const apiUrl = `${this.apiBase}fragment/structure/published?pageId=${selected.pageId}&path=/dropZone&contentFormat=markup&excludeArticles[]=${articleId}&count=0`;

    if (boxTitle) {
      container.querySelector('.teaser-box__title').innerText = boxTitle;
    }
    if (container) {
      const observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !hasLoaded) {
              fetch(apiUrl).then(res => res.text())
              .then(data => {
                container.insertAdjacentHTML('beforeend', data);
                this.emit('new-content-loaded', container);
              });
              hasLoaded = true;
            }
          });
        },
        { rootMargin: '0px 0px 300px 0px' }
      );
      observer.observe(container);
    }
  }
}
